import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import {
    ChartBarIcon,
    DocumentTextIcon,
    ClipboardDocumentListIcon,
    HomeIcon,
    AcademicCapIcon,
    Squares2X2Icon,
    UsersIcon,
    Bars3Icon,
} from '@heroicons/react/24/outline';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';

import ScholarRoster from './ScholarRoster';
import RecordOfWorkTab from '../../components/teacher/RecordOfWorkTab';
import SOWTrackerTab from '../../components/teacher/SOWTrackerTab';
import LessonPlansTab from '../../components/teacher/LessonPlansTab';
import TeacherClassesGradingTab from '../../components/TeacherManagement';
import PremiumTooltip from '../../components/common/PremiumTooltip';
import FilterDropdown from '../../components/common/FilterDropdown';
import CompareControls from '../../components/dashboard/CompareControls';
import { calculate8Level } from '../../utils/cbcUtils';

/* ─────────────────────────────────────────────────────────────────────────
   Shared micro-components
───────────────────────────────────────────────────────────────────────── */

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 17) return 'Good afternoon';
    if (hour >= 17 && hour < 21) return 'Good evening';
    return 'Welcome back';
};

const BandCard = ({ label, count, color, bgColor, icon, expanded, onClick }) => (
    <button 
        onClick={onClick}
        className={`rounded-[2rem] border ${expanded ? 'border-[#18216D] shadow-md scale-[1.02]' : 'border-transparent hover:border-slate-200'} ${bgColor} p-6 transition-all text-center group min-w-[160px] md:min-w-0 flex flex-col items-center h-full w-full`}
    >
        <div className="flex items-center justify-center mb-2">
            <div className="h-10 w-10 bg-white/80 rounded-xl flex items-center justify-center shadow-sm">
                <i className={`fas ${icon} ${color} text-sm`}></i>
            </div>
        </div>
        <div className={`text-4xl font-black ${color} tracking-tighter`}>{count}</div>
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-2 leading-tight w-full break-words">{label}</div>
    </button>
);

const SidebarItem = ({ id, label, icon: Icon, active, collapsed, onClick }) => {
    const content = (
        <button
            onClick={() => onClick(id)}
            className={`w-full flex items-center p-3 mb-2 rounded-xl transition-all group ${active
                ? 'bg-[#18216D] text-white shadow-lg shadow-indigo-900/30'
                : 'text-slate-400 hover:bg-slate-50 hover:text-[#18216D]'
                }`}
        >
            <div className={`flex items-center justify-center ${collapsed ? 'w-full' : ''}`}>
                <Icon className={`w-6 h-6 transition-transform group-hover:scale-110 ${active ? 'text-[#FFD700]' : 'text-slate-300 group-hover:text-[#18216D]'}`} />
            </div>
            {!collapsed && (
                <span className="ml-3 font-bold text-sm tracking-tight whitespace-nowrap overflow-hidden transition-all duration-300">
                    {label}
                </span>
            )}
            {active && !collapsed && (
                <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
            )}
        </button>
    );

    if (collapsed) {
        return (
            <PremiumTooltip text={label} position="right" wrapperClassName="relative block w-full">
                {content}
            </PremiumTooltip>
        );
    }
    return content;
};

const Sidebar = ({ activeTab, onChange, collapsed, onToggle }) => {
    const items = [
        { id: 'overview', label: 'Overview', icon: HomeIcon },
        { id: 'performance', label: 'Performance', icon: UsersIcon },
        { id: 'classes-grading', label: 'Classes & Grading', icon: AcademicCapIcon },
        { id: 'sow', label: 'Schemes of Work', icon: Squares2X2Icon },
        { id: 'lesson-plans', label: 'Lesson Plans', icon: ClipboardDocumentListIcon },
        { id: 'records', label: 'Records of Work', icon: DocumentTextIcon },
    ];

    return (
        <div className={`flex flex-col h-full bg-white border-r border-gray-100 transition-all duration-300 z-50 ${collapsed ? 'hidden md:flex md:w-20' : 'absolute md:relative w-56 md:w-64 shadow-2xl md:shadow-none'}`}>
            <div className="p-4 flex items-center justify-between border-b border-slate-100/50 mb-2">
                {!collapsed && <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest px-2">Navigation</span>}
                <button onClick={onToggle} className={`p-2 hover:bg-slate-50 rounded-lg text-slate-400 transition-colors ${collapsed ? 'mx-auto' : ''}`}>
                    <svg className={`w-5 h-5 transition-transform ${collapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                </button>
            </div>
            <div className="px-4 flex-1 overflow-visible">
                {items.map((item) => (
                    <SidebarItem
                        key={item.id}
                        {...item}
                        active={activeTab === item.id}
                        collapsed={collapsed}
                        onClick={onChange}
                    />
                ))}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Main HOS Dashboard
───────────────────────────────────────────────────────────────────────── */

const HOSDashboard = () => {
    const { get } = useApi();
    const { showToast, performanceBands } = useAppState();
    const { user } = useAuth();
    const [activeNav, setActiveNav] = useState('overview');
    const [selectedTerm, setSelectedTerm] = useState('');
    const [comparisonFilters, setComparisonFilters] = useState({});
    const [expandedBand, setExpandedBand] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 768);

    // Support for Tab Deep-Linking (e.g., ?tab=sow)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab) setActiveNav(tab);
    }, []);

    // Auto-collapse sidebar on mobile when switching tabs
    useEffect(() => {
        if (window.innerWidth < 768) {
            setIsSidebarCollapsed(true);
        }
    }, [activeNav]);

    /* ── Terms ── */
    const { data: terms = [] } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 30 * 60 * 1000,
    });

    useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const current = terms.find(t => t.is_current) || terms[0];
            if (current) setSelectedTerm(current.id.toString());
        }
    }, [terms, selectedTerm]);

    /* ── Learning Areas ── */
    useQuery({
        queryKey: ['learning-areas-all'],
        queryFn: () => get('/api/cbc/learning-areas/'),
        staleTime: 15 * 60 * 1000,
    });

    /* ─────── Nav: Overview ─────── */
    const targetTermId = comparisonFilters.target_term_id || selectedTerm;
    
    const { data: hosStats, isLoading: statsLoading } = useQuery({
        queryKey: ['hos-dashboard-stats', targetTermId],
        queryFn: () => get(`/api/teacher/hos/dashboard/${targetTermId ? `?term_id=${targetTermId}` : ''}`),
        enabled: activeNav === 'overview' && !!targetTermId,
        staleTime: 5 * 60 * 1000,
        onError: () => showToast('Failed to load dashboard stats', 'error'),
    });

    const { data: hosCompareStats } = useQuery({
        queryKey: ['hos-dashboard-stats-compare', comparisonFilters.compare_term_id],
        queryFn: () => get(`/api/teacher/hos/dashboard/?term_id=${comparisonFilters.compare_term_id}`),
        enabled: activeNav === 'overview' && !!comparisonFilters.compare_term_id,
        staleTime: 5 * 60 * 1000,
    });

    const isComparisonEnabled = !!comparisonFilters.compare_term_id;

    const chartData = useMemo(() => {
        const streamMap = new Map();
        
        // Add target data
        if (hosStats?.stream_chart) {
            hosStats.stream_chart.forEach(s => {
                streamMap.set(s.stream, { 
                    stream: s.stream, 
                    avg_score: s.avg_score, 
                    student_count: s.student_count,
                    level: calculate8Level(s.avg_score, performanceBands)
                });
            });
        }
        
        // Add compare data
        if (hosCompareStats?.stream_chart) {
            hosCompareStats.stream_chart.forEach(s => {
                if (!streamMap.has(s.stream)) {
                    streamMap.set(s.stream, { stream: s.stream });
                }
                const entry = streamMap.get(s.stream);
                entry.compare_avg_score = s.avg_score;
                entry.compare_student_count = s.student_count;
                entry.compare_level = calculate8Level(s.avg_score, performanceBands);
            });
        }
        
        return Array.from(streamMap.values()).sort((a, b) => (b.avg_score || 0) - (a.avg_score || 0));
    }, [hosStats, hosCompareStats, performanceBands]);

    /* ─────────────────────────────────────────────────────
       Render helpers
    ───────────────────────────────────────────────────── */

    /* ─────────────────────────────────────────────────────
       Page sections
    ───────────────────────────────────────────────────── */

    const renderOverview = () => {
        const bands = hosStats?.band_counts || { EE: 0, ME: 0, AE: 0, BE: 0 };
        const detailedBands = hosStats?.detailed_band_counts || {};
        const streamChart = chartData;

        const bandConfig = [
            { key: 'EE', label: 'Exceeding (EE)', icon: 'fa-crown', bgColor: 'bg-[#FFD700]/5', color: 'text-[#FFD700]' },
            { key: 'ME', label: 'Meeting (ME)', icon: 'fa-check-double', bgColor: 'bg-[#40E0D0]/5', color: 'text-[#40E0D0]' },
            { key: 'AE', label: 'Approaching (AE)', icon: 'fa-shoe-prints', bgColor: 'bg-[#E97451]/5', color: 'text-[#E97451]' },
            { key: 'BE', label: 'Below (BE)', icon: 'fa-triangle-exclamation', bgColor: 'bg-[#808080]/5', color: 'text-[#808080]' },
        ];

        return (
            <div className="space-y-6">
                {/* Greeting Header wrapped in Overview */}
                <header className="relative bg-[#18216D] rounded-[2rem] p-6 md:p-8 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl overflow-hidden pointer-events-none" />
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Head of Studies Dashboard</p>
                                <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tighter italic leading-none">
                                    {getGreeting()}, <span className="text-[#FFC425]">{user?.first_name || 'HOS'}</span>
                                </h1>
                            </div>
                            <div className="h-2 w-24 bg-[#FFC425] rounded-full" />
                        </div>
                        <div className="flex gap-4">
                            <FilterDropdown
                                value={selectedTerm}
                                onChange={(val) => setSelectedTerm(val)}
                                options={terms.map(t => ({ name: t.name, value: t.id }))}
                                placeholder={terms.find(t => t.is_current)?.name || "Current Term"}
                                className="w-36 z-50"
                            />
                        </div>
                    </div>
                </header>

                {/* Band Cards + School Average */}
                <div className="space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {bandConfig.map(({ key, label, icon, bgColor, color }) => (
                            <BandCard
                                key={key}
                                label={label}
                                count={statsLoading ? '...' : (bands[key] ?? 0)}
                                icon={icon}
                                bgColor={bgColor}
                                color={color}
                                expanded={expandedBand === key}
                                onClick={() => setExpandedBand(expandedBand === key ? null : key)}
                            />
                        ))}
                    </div>

                    {/* Detailed Sub-Cards when a band is clicked */}
                    {expandedBand && (
                        <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-4 duration-300">
                            {['1', '2'].map(suffix => {
                                const detailKey = `${expandedBand}${suffix}`;
                                const activeBand = bandConfig.find(b => b.key === expandedBand);
                                return (
                                    <div key={detailKey} className={`rounded-[2rem] border border-transparent ${activeBand?.bgColor} hover:shadow-md transition-all p-6 flex flex-col items-center justify-center`}>
                                        <div className="text-[11px] font-black uppercase tracking-widest text-[#18216D] mb-3 text-center">
                                            {activeBand?.label.split(' ')[0]} {detailKey}
                                        </div>
                                        <div className={`text-4xl font-black tracking-tighter ${activeBand?.color}`}>
                                            {detailedBands[detailKey] || 0}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>



                {/* Stream Performance Chart */}
                <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-slate-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                        <div>
                            <h2 className="text-2xl font-black text-[#18216D] tracking-tight italic">Performance by Stream</h2>
                            <p className="text-slate-400 font-bold text-xs mt-1 lowercase tracking-widest">
                                {isComparisonEnabled 
                                    ? "Comparative average score per class stream" 
                                    : "Average score per class stream across the school"}
                            </p>
                        </div>
                        <CompareControls onFilterChange={setComparisonFilters} />
                    </div>

                    <div className="flex items-center gap-6 mb-6 px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-[#18216D]"></div>
                            <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                {terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period'}
                            </span>
                        </div>
                        {isComparisonEnabled && (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded bg-[#FFC425]"></div>
                                <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">
                                    {comparisonFilters.compare_term_id 
                                        ? (terms.find(t => t.id.toString() === comparisonFilters.compare_term_id.toString())?.name || 'Comparison Term')
                                        : 'Comparison Year'
                                    }
                                </span>
                            </div>
                        )}
                    </div>

                    {statsLoading ? (
                        <div className="h-80 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#18216D]" />
                        </div>
                    ) : streamChart.length > 0 ? (
                        <div className="h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={streamChart} margin={{ top: 10, right: 20, left: 10, bottom: 80 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="stream"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }}
                                        angle={-40}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        domain={[0, 100]}
                                        tickFormatter={v => `${v}%`}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(24,33,109,0.03)' }}
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                const d = streamChart.find(c => c.stream === label);
                                                const isComparison = payload.length > 1;
                                                return (
                                                    <div className="bg-white/95 backdrop-blur-md p-4 rounded-3xl shadow-2xl border border-slate-100 min-w-[160px] pointer-events-none">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 border-b border-slate-100 pb-2">{label}</p>
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between gap-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-2 h-2 rounded-full bg-[#18216D]"></div>
                                                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                                        {terms.find(t => t.id.toString() === (comparisonFilters.target_term_id || selectedTerm))?.name || 'Target Period'}
                                                                    </p>
                                                                </div>
                                                                <div className="text-right flex flex-col items-end">
                                                                    <div>
                                                                        <span className="text-sm font-black text-[#18216D] italic">{d?.level}</span>
                                                                        <span className="text-[9px] font-bold text-slate-300 ml-1.5">{d?.avg_score}%</span>
                                                                    </div>
                                                                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{d?.student_count} students</span>
                                                                </div>
                                                            </div>
                                                            {isComparison && d?.compare_avg_score !== undefined && (
                                                                <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-50">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-2 h-2 rounded-full bg-[#FFC425]"></div>
                                                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                                            {comparisonFilters.compare_term_id 
                                                                                ? (terms.find(t => t.id.toString() === comparisonFilters.compare_term_id.toString())?.name || 'Comparison')
                                                                                : 'Comparison Year'
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right flex flex-col items-end">
                                                                        <div>
                                                                            <span className="text-sm font-black text-[#18216D] italic">{d?.compare_level}</span>
                                                                            <span className="text-[9px] font-bold text-slate-300 ml-1.5">{d?.compare_avg_score}%</span>
                                                                        </div>
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{d?.compare_student_count} students</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar 
                                        dataKey="avg_score" 
                                        fill="#18216D" 
                                        radius={[4, 4, 0, 0]} 
                                        barSize={isComparisonEnabled ? 16 : 32}
                                        background={{ fill: '#f8fafc', radius: [4, 4, 0, 0] }}
                                    >
                                        {!isComparisonEnabled && streamChart.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.avg_score >= 75 ? '#10b981' : entry.avg_score >= 50 ? '#18216D' : entry.avg_score >= 25 ? '#FFC425' : '#ef4444'}
                                            />
                                        ))}
                                    </Bar>
                                    {isComparisonEnabled && (
                                        <Bar 
                                            dataKey="compare_avg_score" 
                                            fill="#FFC425" 
                                            radius={[4, 4, 0, 0]} 
                                            barSize={16}
                                        />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-80 flex flex-col items-center justify-center">
                            <ChartBarIcon className="w-16 h-16 text-slate-100 mb-4" />
                            <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No performance data available for the selected term</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Lesson plans are now handled by LessonPlansTab component

    return (
        <div className="flex h-[calc(100vh-4rem)] bg-gray-50 overflow-hidden relative">
            <Sidebar
                activeTab={activeNav}
                onChange={(tab) => setActiveNav(tab)}
                collapsed={isSidebarCollapsed}
                onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            />

            <div className="flex-1 flex flex-col min-w-0 overflow-y-auto relative">
                <main className="flex-1 p-4 md:p-8 relative">
                    <button 
                        onClick={() => setIsSidebarCollapsed(false)}
                        className={`md:hidden fixed bottom-6 right-6 z-50 p-4 bg-[#18216D] text-white rounded-full shadow-2xl ${!isSidebarCollapsed ? 'hidden' : ''}`}
                    >
                        <Bars3Icon className="w-6 h-6" />
                    </button>

                    <div className="max-w-7xl mx-auto">
                        {activeNav === 'overview' && renderOverview()}
                        {activeNav === 'performance' && (
                            <ScholarRoster isHOS={true} />
                        )}
                        {activeNav === 'classes-grading' && (
                            <TeacherClassesGradingTab />
                        )}
                        {activeNav === 'sow' && (
                            <SOWTrackerTab isHOS={true} />
                        )}
                        {activeNav === 'lesson-plans' && (
                            <LessonPlansTab isHOS={true} />
                        )}
                        {activeNav === 'records' && (
                            <RecordOfWorkTab isHOS={true} />
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default HOSDashboard;
